import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { IoInformationCircleOutline, IoRefreshOutline } from "react-icons/io5";
import {
  _listBotKnowledgeSources,
  _addBotKnowledgeSource,
  _removeBotKnowledgeSource,
  _retryBotKnowledgeSource,
} from "Services/api";
import { KnowledgeSourceType } from "constants/react-hook-form";
import KnowledgeSourceItem from "./KnowledgeSourceItem";
import AddKnowledgeSourceForm from "./AddKnowledgeSourceForm";

// Re-fetch the list on this cadence while any source is still processing, so
// per-source status stays live even though listed sources carry no task_id.
const LIST_POLL_INTERVAL_MS = 4000;

// Mirrors the server-side compiled-blob cap; used only to warn in the UI.
const COMPILED_MAX_BYTES = 250000;

const isInProgress = (status) => status === "pending" || status === "processing";

// The list endpoint may key the id as `id` (contract) — tolerate `source_id` too.
const getSourceId = (source) => source?.id ?? source?.source_id;

// Friendly copy for the machine-readable error codes in the response envelope.
const ERROR_MESSAGE_BY_CODE = {
  KNOWLEDGE_SOURCE_NOT_FOUND: "That source no longer exists. Refreshing the list.",
};

const resolveErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  return ERROR_MESSAGE_BY_CODE[data?.data?.code] || data?.message || fallback;
};

const KnowledgeSourcesPanel = ({ botId, refreshKey }) => {
  const accessToken = sessionStorage.getItem("VendorToken");
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [pendingIds, setPendingIds] = useState({}); // { [sourceId]: "retrying" | "removing" }
  // task_id per source captured from add/retry responses this session, used to
  // enrich the row with a live progress % (listed sources have no task_id).
  const [taskIdBySource, setTaskIdBySource] = useState({});
  const hasLoadedOnce = useRef(false);

  const activeSources = useMemo(
    () => sources.filter((source) => source?.is_active !== false),
    [sources]
  );

  const hasAutoSeeded = useMemo(
    () => activeSources.some((source) => source?.origin === "auto"),
    [activeSources]
  );

  // List-poll only for in-progress sources we CAN'T track via /task-status
  // (e.g. after a reload, when the session task_id is gone). Sources with a
  // live task_id are driven by the row's task-status poll instead, which
  // triggers a single list refetch on completion — so we don't hammer the
  // list endpoint alongside it.
  const needsListPoll = useMemo(
    () =>
      activeSources.some(
        (source) => isInProgress(source?.status) && !taskIdBySource[getSourceId(source)]
      ),
    [activeSources, taskIdBySource]
  );

  const compiledBytes = useMemo(
    () =>
      activeSources
        .filter((source) => source?.status === "completed")
        .reduce((sum, source) => sum + (source?.byte_size || 0), 0),
    [activeSources]
  );
  const compiledPct = Math.min(100, Math.round((compiledBytes / COMPILED_MAX_BYTES) * 100));
  const nearCap = compiledBytes >= COMPILED_MAX_BYTES * 0.9;

  const fetchSources = useCallback(
    async ({ silent = false } = {}) => {
      if (!botId) return;
      if (!silent) setLoading(true);
      try {
        const response = await _listBotKnowledgeSources({ botId, accessToken });
        if (response?.status) {
          setSources(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        // Don't spam toasts on background polls; only surface the initial load.
        if (!silent) {
          toast.error(resolveErrorMessage(error, "Could not load knowledge sources."));
        }
        console.error("Error loading knowledge sources:", error);
      } finally {
        if (!silent) setLoading(false);
        hasLoadedOnce.current = true;
      }
    },
    [botId, accessToken]
  );

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  // Refetch when the parent signals an owner change (auto sources may have
  // been seeded/removed server-side). Skip the initial mount — the effect
  // above already loads on first render.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    fetchSources({ silent: true });
  }, [refreshKey, fetchSources]);

  // Keep the list fresh while an untracked source is still processing.
  useEffect(() => {
    if (!needsListPoll) return undefined;
    const intervalId = setInterval(() => fetchSources({ silent: true }), LIST_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [needsListPoll, fetchSources]);

  // A row's /task-status poll reached a terminal state. Drop the tracked
  // task_id and pull the authoritative row once. Clearing the task_id means
  // that if the list momentarily still shows the source in progress (a brief
  // race between task-status and the row's status column), needsListPoll
  // re-engages as a fallback until it settles — otherwise we'd poll neither.
  const handleTaskComplete = useCallback(
    (sourceId) => {
      if (sourceId) {
        setTaskIdBySource((prev) => {
          if (!prev[sourceId]) return prev;
          const next = { ...prev };
          delete next[sourceId];
          return next;
        });
      }
      fetchSources({ silent: true });
    },
    [fetchSources]
  );

  const handleAdd = useCallback(
    async (values) => {
      if (!botId) {
        toast.error("Create the bot first.");
        return false;
      }
      setIsAdding(true);
      try {
        const sourceType = values.sourceType;
        const payload = {
          botId,
          accessToken,
          title: values.title || undefined,
          url: sourceType === KnowledgeSourceType.WEBSITE ? values.url : undefined,
          file: sourceType === KnowledgeSourceType.PDF ? values.file : undefined,
          text: sourceType === KnowledgeSourceType.MANUAL ? values.text : undefined,
        };
        const response = await _addBotKnowledgeSource(payload);
        if (!response?.status) {
          toast.error(response?.message || "Could not add the source.");
          return false;
        }
        const newSourceId = response?.data?.source_id;
        const newTaskId = response?.data?.task_id;
        if (newSourceId && newTaskId) {
          setTaskIdBySource((prev) => ({ ...prev, [newSourceId]: newTaskId }));
        }
        toast.success(response?.message || "Source added.");
        // Re-fetch to pull the authoritative new row (list has no task_id, so
        // the poll above keeps it live from here).
        await fetchSources({ silent: true });
        return true;
      } catch (error) {
        toast.error(resolveErrorMessage(error, "Could not add the source."));
        return false;
      } finally {
        setIsAdding(false);
      }
    },
    [botId, accessToken, fetchSources]
  );

  const handleRetry = useCallback(
    async (source) => {
      const sourceId = getSourceId(source);
      if (!sourceId) return;
      setPendingIds((prev) => ({ ...prev, [sourceId]: "retrying" }));
      try {
        const response = await _retryBotKnowledgeSource({ botId, sourceId, accessToken });
        if (!response?.status) {
          toast.error(response?.message || "Could not retry the source.");
          return;
        }
        if (response?.data?.task_id) {
          setTaskIdBySource((prev) => ({ ...prev, [sourceId]: response.data.task_id }));
        }
        toast.success(response?.message || "Reprocessing started.");
        await fetchSources({ silent: true });
      } catch (error) {
        const message = resolveErrorMessage(error, "Could not retry the source.");
        toast.error(message);
        if (error?.response?.status === 404) {
          await fetchSources({ silent: true });
        }
      } finally {
        setPendingIds((prev) => {
          const next = { ...prev };
          delete next[sourceId];
          return next;
        });
      }
    },
    [botId, accessToken, fetchSources]
  );

  const handleRemove = useCallback(
    async (source) => {
      const sourceId = getSourceId(source);
      if (!sourceId) return;

      const isAuto = source?.origin === "auto";
      const confirmMessage = isAuto
        ? "Remove this auto-added source? It won't come back automatically the next time this owner is linked."
        : "Remove this knowledge source?";
      // eslint-disable-next-line no-alert
      if (!window.confirm(confirmMessage)) return;

      setPendingIds((prev) => ({ ...prev, [sourceId]: "removing" }));
      try {
        const response = await _removeBotKnowledgeSource({ botId, sourceId, accessToken });
        if (!response?.status) {
          toast.error(response?.message || "Could not remove the source.");
          return;
        }
        toast.success(response?.message || "Source removed.");
        // Drop it locally right away, then reconcile with the server.
        setSources((prev) => prev.filter((item) => getSourceId(item) !== sourceId));
        await fetchSources({ silent: true });
      } catch (error) {
        const message = resolveErrorMessage(error, "Could not remove the source.");
        toast.error(message);
        // 404 = already gone; refresh so the stale row disappears.
        if (error?.response?.status === 404) {
          await fetchSources({ silent: true });
        }
      } finally {
        setPendingIds((prev) => {
          const next = { ...prev };
          delete next[sourceId];
          return next;
        });
      }
    },
    [botId, accessToken, fetchSources]
  );

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm text-gray-500">
          Add the pages, documents, and notes your bot should answer from.
        </p>
        <button
          type="button"
          onClick={() => fetchSources()}
          className="rounded border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50"
          title="Refresh"
          aria-label="Refresh sources"
        >
          <IoRefreshOutline className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <AddKnowledgeSourceForm onSubmit={handleAdd} isSubmitting={isAdding} />

      {hasAutoSeeded && (
        <div className="mb-3 flex items-start gap-2 rounded-md bg-indigo-50 p-2.5 text-xs text-indigo-700">
          <IoInformationCircleOutline className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Sources marked <b>Auto</b> were added automatically from your Plist storefront and
            organization website. You can remove them if you don't want the bot to use them.
          </span>
        </div>
      )}

      {activeSources.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Compiled knowledge size</span>
            <span className={nearCap ? "font-semibold text-amber-600" : ""}>
              {(compiledBytes / 1000).toFixed(0)} / {COMPILED_MAX_BYTES / 1000} KB
            </span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className={`h-full rounded-full transition-all ${
                nearCap ? "bg-amber-500" : "bg-blue-500"
              }`}
              style={{ width: `${compiledPct}%` }}
            />
          </div>
          {nearCap && (
            <p className="mt-1 text-xs text-amber-600">
              Approaching the {COMPILED_MAX_BYTES / 1000} KB limit — lowest-priority sources are
              trimmed first.
            </p>
          )}
        </div>
      )}

      {loading && !hasLoadedOnce.current ? (
        <p className="py-6 text-center text-sm text-gray-400">Loading sources…</p>
      ) : activeSources.length === 0 ? (
        <p className="rounded-md border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">
          No knowledge sources yet. Add a website, PDF, or some text to get started.
        </p>
      ) : (
        <ul className="space-y-2">
          {activeSources.map((source) => {
            const sourceId = getSourceId(source);
            return (
              <KnowledgeSourceItem
                key={sourceId}
                source={source}
                taskId={taskIdBySource[sourceId]}
                accessToken={accessToken}
                onRetry={handleRetry}
                onRemove={handleRemove}
                onTaskComplete={handleTaskComplete}
                isRetrying={pendingIds[sourceId] === "retrying"}
                isRemoving={pendingIds[sourceId] === "removing"}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default KnowledgeSourcesPanel;
